import { useSelector } from "react-redux";

import {
  GDC_SOURCE,
  IHS_SOURCE,
  SHALE_PROFILE_SOURCE
} from "constants/datasource.constants";
import { RootState } from "store/rootReducer";

export default function useDataSouceAvailability() {
  const dataSources = useSelector((state: RootState) => state.auth.dataSources);
  return {
    hasGdc: dataSources ? dataSources.indexOf(GDC_SOURCE) >= 0 : false,
    hasIhs: dataSources ? dataSources.indexOf(IHS_SOURCE) >= 0 : false,
    hasShaleProfile: dataSources ? dataSources.indexOf(SHALE_PROFILE_SOURCE) >= 0 : false
  };
}

/**
 * The data sources each landgrid comes with, which are the survey systems the Jump to
 * Location panel offers a tab for. Names are matched without regard to case, and the
 * extra spellings sit beside the shared constants because the casing the payload uses for
 * Gather is not confirmed, and Novi is the other name the US source goes by. A name on
 * neither list is left to the rule below, which offers both landgrids on purpose rather
 * than leaving the panel with no grid at all.
 */
const CANADIAN_SOURCES = [GDC_SOURCE, IHS_SOURCE, "Gather"];
const US_SOURCES = [SHALE_PROFILE_SOURCE, "Novi"];

/** Which landgrids an organization is offered. */
interface AvailableLandgrids {
  canada: boolean;
  us: boolean;
}

function hasAnySource(dataSources: string[], names: string[]): boolean {
  const wanted = names.map((name) => name.toLowerCase());
  return dataSources.some((source) => wanted.includes(String(source).toLowerCase()));
}

/**
 * The landgrids the organization's data sources come with. A list naming neither side —
 * no sources at all, or only sources this does not know — is read as no answer rather
 * than as none, and offers both, so nobody is left with nowhere to jump.
 */
export function useAvailableLandgrids(): AvailableLandgrids {
  const dataSources = useSelector((state: RootState) => state.auth.dataSources);
  const sources = dataSources ?? [];
  const canada = hasAnySource(sources, CANADIAN_SOURCES);
  const us = hasAnySource(sources, US_SOURCES);
  if (!canada && !us) {
    return { canada: true, us: true };
  }
  return { canada, us };
}
